import { useState } from 'react';
import { DownloadIcon, SecondaryButton, SnackbarStatus, Typography, useSnackbar } from '@weareopenr/component-library';
import { IEventObject } from 'src/api/getEvents';
import styles from './EventDownloads.module.scss';

interface EventDownloadsProps {
  /** Missing on events from an API that doesn't return `objects` yet */
  objects?: IEventObject[];
  /** Refetches the event and returns its objects, since a download link only lasts an hour */
  onRefresh: () => Promise<IEventObject[] | undefined>;
}

// File names include the event id, e.g. `errors_event-1.csv`, so files are matched on their extension
enum EventFileType {
  ARCHIVE = '.tar.gz',
  ERRORS = '.csv',
}

const DOWNLOAD_LABELS: Record<EventFileType, string> = {
  [EventFileType.ARCHIVE]: 'Download sent files',
  [EventFileType.ERRORS]: 'Download errors',
};

export const getDownloadLabel = (name: string) => {
  const fileType = Object.values(EventFileType).find((extension) => name.endsWith(extension));
  return fileType ? DOWNLOAD_LABELS[fileType] : `Download ${name}`;
};

const errorState: Record<string, { status: SnackbarStatus; message: string }> = {
  unavailable: { status: SnackbarStatus.ERROR, message: 'This file is no longer available' },
  failed: { status: SnackbarStatus.ERROR, message: 'Download failed' },
};

const SmallDownloadIcon = () => <DownloadIcon size="small" />;

// The link is presigned with Content-Disposition: attachment, so following it downloads the file
// without a cross-origin fetch.
const startDownload = (url: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  anchor.click();
};

export const EventDownloads = ({ objects, onRefresh }: EventDownloadsProps) => {
  const { showSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);

  if (!objects?.length) return null;

  const download = async (id: string) => {
    setIsDownloading(true);
    try {
      const fresh = (await onRefresh())?.find((object) => object.id === id);
      if (!fresh) {
        showSnackbar(errorState.unavailable);
        return;
      }
      startDownload(fresh.download_url);
    } catch {
      showSnackbar(errorState.failed);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles['event-downloads']}>
      {objects.map(({ id, name }) => (
        <SecondaryButton
          key={id}
          size="small"
          type="button"
          disabled={isDownloading}
          onClick={() => download(id)}
          icon={SmallDownloadIcon}
        >
          <Typography size="small" weight="bold">
            {getDownloadLabel(name)}
          </Typography>
        </SecondaryButton>
      ))}
    </div>
  );
};
